import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { ApiResponseError, post } from '../lib/api';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { InlineAlert } from '../components/ui/States';
import { Wordmark } from '../components/Wordmark';

type Step = 'credentials' | 'enroll' | 'totp' | 'forgot';

interface CredentialsForm {
  email: string;
  password: string;
}

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginBegin, loginVerify, totpEnrollBegin, totpEnrollConfirm, refresh } =
    useAuth();

  const from =
    (location.state as { from?: { pathname: string } } | null)?.from
      ?.pathname ?? '/dashboard/';

  const [step, setStep] = useState<Step>('credentials');
  const [credentials, setCredentials] = useState<CredentialsForm>({
    email: '',
    password: '',
  });
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [enrollmentToken, setEnrollmentToken] = useState<string | null>(null);
  const [enrollQrUri, setEnrollQrUri] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<CredentialsForm & { totp_code: string }>
  >({});
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  function validateCredentials(): boolean {
    const errors: Partial<CredentialsForm> = {};
    if (!credentials.email) errors.email = 'Email is required';
    else if (!/^\S+@\S+\.\S+$/.test(credentials.email))
      errors.email = 'Enter a valid email address';
    if (!credentials.password) errors.password = 'Password is required';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleCredentialsSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!validateCredentials()) return;
    setIsLoading(true);
    try {
      const begin = await loginBegin({
        email: credentials.email,
        password: credentials.password,
      });
      if (begin.path === 'trusted_device') {
        // Remembered device — the server already issued the session cookie
        // and skipped TOTP. Hydrate auth from /me and go straight in.
        await refresh();
        navigate(from, { replace: true });
        return;
      } else if (begin.totp_enrollment_required && begin.enrollment_token) {
        const enroll = await totpEnrollBegin({
          enrollment_token: begin.enrollment_token,
        });
        setEnrollmentToken(begin.enrollment_token);
        setEnrollQrUri(enroll.qr_code_uri);
        setStep('enroll');
      } else if (begin.challenge_id) {
        setChallengeId(begin.challenge_id);
        setStep('totp');
      } else {
        setError('Unexpected response from server. Contact support.');
      }
    } catch (err) {
      if (err instanceof ApiResponseError) {
        setError(err.status === 401 ? 'Invalid email or password.' : err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleEnrollConfirm(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!enrollmentToken) {
      setError('Enrollment session expired. Please sign in again.');
      setStep('credentials');
      return;
    }
    if (!totpCode.trim()) {
      setFieldErrors({ totp_code: 'Authentication code is required' });
      return;
    }
    setFieldErrors({});
    setIsLoading(true);
    try {
      await totpEnrollConfirm({
        enrollment_token: enrollmentToken,
        totp_code: totpCode,
      });
      // The candidate just proved they own the secret by typing a
      // valid TOTP code. Asking them for a *second* code immediately
      // afterwards is jarring. Re-issue the login challenge and
      // verify with the same code in one shot — if the code is still
      // within its 30-second window (it will be, we're milliseconds
      // away from confirm) the candidate is signed in. If the code
      // happens to roll over between confirm and verify, we fall
      // back to the explicit TOTP step.
      const begin = await loginBegin({
        email: credentials.email,
        password: credentials.password,
      });
      if (!begin.challenge_id) {
        setError('Enrollment succeeded but login did not return a challenge.');
        setStep('credentials');
        return;
      }
      try {
        await loginVerify({
          challenge_id: begin.challenge_id,
          totp_code: totpCode,
        });
        navigate(from, { replace: true });
        return;
      } catch (verifyErr) {
        // Same code rejected — most likely it just rolled over.
        // Surface the explicit TOTP step so the candidate enters a
        // fresh code rather than seeing a generic error.
        setChallengeId(begin.challenge_id);
        setEnrollmentToken(null);
        setEnrollQrUri(null);
        setTotpCode('');
        setStep('totp');
        if (verifyErr instanceof ApiResponseError && verifyErr.status === 401) {
          setError(
            'Your code rolled over between enrollment and sign-in. Please enter the new 6-digit code from your authenticator.',
          );
        }
        return;
      }
    } catch (err) {
      if (err instanceof ApiResponseError) {
        setError(
          err.status === 401
            ? 'Invalid authentication code. Please re-scan and try again.'
            : err.message
        );
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleTotpSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!challengeId) {
      setError('Login session expired. Please sign in again.');
      setStep('credentials');
      return;
    }
    if (!totpCode.trim()) {
      setFieldErrors({ totp_code: 'Authentication code is required' });
      return;
    }
    setFieldErrors({});
    setIsLoading(true);
    try {
      await loginVerify({
        challenge_id: challengeId,
        totp_code: totpCode,
        trust_device: rememberDevice,
      });
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiResponseError) {
        setError(err.status === 401 ? 'Invalid authentication code.' : err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleForgotSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmed = forgotEmail.trim();
    if (!trimmed) {
      setError('Enter your email.');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(trimmed)) {
      setError("That doesn't look like an email address.");
      return;
    }
    setIsLoading(true);
    try {
      await post('/auth/password/forgot', { email: trimmed });
      setForgotSent(true);
    } catch (err) {
      setError(
        err instanceof ApiResponseError
          ? err.message
          : 'Could not send the reset link. Try again.',
      );
    } finally {
      setIsLoading(false);
    }
  }

  function backToCredentials() {
    setStep('credentials');
    setChallengeId(null);
    setEnrollmentToken(null);
    setEnrollQrUri(null);
    setTotpCode('');
    setForgotSent(false);
    setError(null);
  }

  return (
    <div className="grid min-h-screen grid-cols-1 bg-[var(--color-bg)] lg:grid-cols-[1.05fr_1fr]">
      {/* ── Left panel: brand gradient hero ───────────────────────── */}
      <aside className="relative hidden flex-col justify-between overflow-hidden bg-gradient-navy-blue px-12 py-14 text-white lg:flex xl:px-20 xl:py-20">
        {/* Decorative geometric overlay — abstract data motif, very subtle */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 80% 20%, white 0, transparent 40%), radial-gradient(circle at 20% 80%, white 0, transparent 40%)',
          }}
        />

        <div className="relative z-10">
          <Wordmark size={28} inverse />
        </div>

        <div className="relative z-10 max-w-[520px]">
          <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70 mb-6">
            Staff Workbench
          </p>
          <h1 className="display text-white text-[60px] xl:text-[72px]">
            Evidence&nbsp;before
            <br />
            <span className="text-[var(--color-success)]">decision.</span>
          </h1>
          <p className="mt-7 max-w-[420px] font-sans text-[15px] leading-relaxed text-white/80">
            Prism is a competency-based assessment instrument for Learning
            and Development and Instructional Design hires. Every classification
            and every recommendation is reviewed by a human assessor before
            it leaves this workbench.
          </p>
        </div>

        <div className="relative z-10 flex items-center gap-3 font-mono text-[10.5px] uppercase tracking-[0.18em] text-white/60">
          <span>Mediant Labs</span>
          <span aria-hidden="true">·</span>
          <span>GDPR Article 22 safeguards in place</span>
        </div>
      </aside>

      {/* ── Right panel: form ─────────────────────────────────────── */}
      <main className="flex items-center justify-center px-6 py-12 sm:px-10">
        <div className="w-full max-w-[420px] prism-enter">
          {/* Mobile wordmark (left panel hidden on small screens) */}
          <div className="mb-10 lg:hidden">
            <Wordmark size={28} />
          </div>

          {step === 'credentials' && (
            <>
              <p className="eyebrow mb-3">Assessor sign-in</p>
              <h2 className="text-[34px] font-bold leading-tight tracking-[-0.02em] text-[var(--color-ink)]">
                Welcome Back.
              </h2>
              <p className="mt-3 text-[14.5px] leading-relaxed text-[var(--color-ink-soft)]">
                Use the credentials issued by your administrator. First-time
                sign-in walks you through enrolling an authenticator app.
              </p>

              <form onSubmit={handleCredentialsSubmit} noValidate className="mt-10 space-y-6">
                <Input
                  label="Email address"
                  type="email"
                  autoComplete="email"
                  value={credentials.email}
                  onChange={(e) =>
                    setCredentials((p) => ({ ...p, email: e.target.value }))
                  }
                  error={fieldErrors.email}
                  disabled={isLoading}
                  required
                />
                <Input
                  label="Password"
                  type="password"
                  autoComplete="current-password"
                  value={credentials.password}
                  onChange={(e) =>
                    setCredentials((p) => ({ ...p, password: e.target.value }))
                  }
                  error={fieldErrors.password}
                  disabled={isLoading}
                  required
                />
                <div className="-mt-2 text-right">
                  <button
                    type="button"
                    onClick={() => {
                      setError(null);
                      setFieldErrors({});
                      setForgotEmail(credentials.email);
                      setForgotSent(false);
                      setStep('forgot');
                    }}
                    className="font-sans text-[13px] text-[var(--color-ink-muted)] underline-offset-4 hover:text-[var(--color-primary)] hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
                {error && <InlineAlert variant="danger">{error}</InlineAlert>}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  isLoading={isLoading}
                  className="w-full"
                >
                  Continue
                </Button>
              </form>
            </>
          )}

          {step === 'forgot' && (
            <>
              <p className="eyebrow mb-3">Reset password</p>
              <h2 className="text-[34px] font-bold leading-tight tracking-[-0.02em] text-[var(--color-ink)]">
                {forgotSent ? 'Check Your Inbox.' : 'Forgot Your Password?'}
              </h2>
              {forgotSent ? (
                <>
                  <p className="mt-3 text-[14.5px] leading-relaxed text-[var(--color-ink-soft)]">
                    If that email matches a staff account, a reset link is on
                    its way. It expires in 1 hour. After you set a new password
                    you'll sign in again with your password and your
                    authenticator code.
                  </p>
                  <button
                    type="button"
                    onClick={backToCredentials}
                    className="mt-8 font-sans text-[14px] font-medium text-[var(--color-primary)] underline-offset-4 hover:underline"
                  >
                    ← Back to sign in
                  </button>
                </>
              ) : (
                <>
                  <p className="mt-3 text-[14.5px] leading-relaxed text-[var(--color-ink-soft)]">
                    Enter the email for your staff account and we'll send a
                    one-hour reset link. You'll set a new password, then sign in
                    again with that password and your authenticator code.
                  </p>
                  <form
                    onSubmit={handleForgotSubmit}
                    noValidate
                    className="mt-10 space-y-6"
                  >
                    <Input
                      label="Email address"
                      type="email"
                      autoComplete="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      disabled={isLoading}
                      required
                      autoFocus
                    />
                    {error && <InlineAlert variant="danger">{error}</InlineAlert>}
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      isLoading={isLoading}
                      className="w-full"
                    >
                      Send reset link
                    </Button>
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={backToCredentials}
                        className="font-sans text-[13px] text-[var(--color-ink-muted)] underline-offset-4 hover:text-[var(--color-primary)] hover:underline"
                      >
                        ← Back to sign in
                      </button>
                    </div>
                  </form>
                </>
              )}
            </>
          )}

          {step === 'enroll' && (
            <>
              <p className="eyebrow mb-3">Two-factor enrolment</p>
              <h2 className="text-[34px] font-bold leading-tight tracking-[-0.02em] text-[var(--color-ink)]">
                Set Up Your Authenticator.
              </h2>
              <p className="mt-3 text-[14.5px] leading-relaxed text-[var(--color-ink-soft)]">
                Scan the QR code with Google Authenticator, 1Password, Authy,
                or any TOTP app. Then enter the six-digit code it shows.
              </p>

              {enrollQrUri && (
                <div className="mx-auto mt-8 w-fit rounded-[6px] border border-[var(--color-rule-strong)] bg-white p-3">
                  <img
                    alt="TOTP enrolment QR code"
                    src={enrollQrUri}
                    className="block h-48 w-48"
                  />
                </div>
              )}

              <form onSubmit={handleEnrollConfirm} noValidate className="mt-8 space-y-6">
                <Input
                  label="Six-digit code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={totpCode}
                  onChange={(e) =>
                    setTotpCode(e.target.value.replace(/\D/g, ''))
                  }
                  error={fieldErrors.totp_code}
                  disabled={isLoading}
                  autoFocus
                  required
                />
                {error && <InlineAlert variant="danger">{error}</InlineAlert>}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  isLoading={isLoading}
                  className="w-full"
                >
                  Confirm enrolment
                </Button>
                <button
                  type="button"
                  onClick={backToCredentials}
                  className="block w-full cursor-pointer text-center font-sans text-[13px] text-[var(--color-ink-muted)] underline-offset-4 hover:text-[var(--color-primary)] hover:underline"
                >
                  ← Back
                </button>
              </form>
            </>
          )}

          {step === 'totp' && (
            <>
              <p className="eyebrow mb-3">Two-factor verification</p>
              <h2 className="text-[34px] font-bold leading-tight tracking-[-0.02em] text-[var(--color-ink)]">
                One More Thing.
              </h2>
              <p className="mt-3 text-[14.5px] leading-relaxed text-[var(--color-ink-soft)]">
                Open your authenticator app and enter the six-digit code.
              </p>
              <form onSubmit={handleTotpSubmit} noValidate className="mt-10 space-y-6">
                <Input
                  label="Six-digit code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={totpCode}
                  onChange={(e) =>
                    setTotpCode(e.target.value.replace(/\D/g, ''))
                  }
                  error={fieldErrors.totp_code}
                  disabled={isLoading}
                  autoFocus
                  required
                />
                <label className="flex cursor-pointer items-center gap-2.5 font-sans text-[13.5px] text-[var(--color-ink-muted)]">
                  <input
                    type="checkbox"
                    checked={rememberDevice}
                    onChange={(e) => setRememberDevice(e.target.checked)}
                    disabled={isLoading}
                    className="h-4 w-4 rounded border-[var(--color-rule-strong)] text-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary)]/30"
                  />
                  Trust this device for 30 days (skip the code next time)
                </label>
                {error && <InlineAlert variant="danger">{error}</InlineAlert>}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  isLoading={isLoading}
                  className="w-full"
                >
                  Sign in
                </Button>
                <button
                  type="button"
                  onClick={backToCredentials}
                  className="block w-full cursor-pointer text-center font-sans text-[13px] text-[var(--color-ink-muted)] underline-offset-4 hover:text-[var(--color-primary)] hover:underline"
                >
                  ← Back
                </button>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
